import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, Stack, Select, MenuItem,
  TextField, Button, CircularProgress, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, Tooltip,
  FormControl, Collapse, Chip, useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TuneIcon from '@mui/icons-material/Tune';
import HistoryIcon from '@mui/icons-material/History';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import api from '../api';

const PROVIDERS = {
  openai: {
    label: 'OpenAI', color: '#10a37f',
    models: [
      { id: 'gpt-4o', label: 'GPT-4o' },
      { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
      { id: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
      { id: 'o3-mini', label: 'o3-mini' },
    ],
  },
  anthropic: {
    label: 'Anthropic', color: '#d97706',
    models: [
      { id: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
      { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
      { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    ],
  },
  gemini: {
    label: 'Google Gemini', color: '#4285f4',
    models: [
      { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
      { id: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
      { id: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
    ],
  },
  bedrock: {
    label: 'Amazon Bedrock', color: '#FF9900',
    models: [
      { id: 'global.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Claude Sonnet 4.5', group: 'Anthropic' },
    ],
  },
};

const selectSx = {
  color: 'var(--tx-md)', fontSize: '0.78rem',
  bgcolor: 'rgba(255,255,255,0.04)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99,102,241,0.4)' },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
};

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function renderInline(text) {
  return text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**'))
      return <strong key={i} style={{ color: 'var(--tx-md)' }}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('`') && p.endsWith('`'))
      return <code key={i} style={{ fontFamily: 'monospace', fontSize: '0.82em', color: '#93c5fd', background: 'rgba(147,197,253,0.1)', borderRadius: 3, padding: '1px 4px' }}>{p.slice(1, -1)}</code>;
    return p;
  });
}

function MessageText({ text }) {
  const lines = text.split('\n');
  const els = [];
  let codeLines = null;
  lines.forEach((line, i) => {
    if (line.startsWith('```')) {
      if (codeLines === null) { codeLines = []; }
      else {
        els.push(
          <Box key={'cb' + i} sx={{ bgcolor: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 1.5, p: 1.5, my: 0.75, overflowX: 'auto' }}>
            <Typography component="pre" sx={{ m: 0, fontSize: '0.75rem', color: '#93c5fd', fontFamily: 'monospace', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{codeLines.join('\n')}</Typography>
          </Box>
        );
        codeLines = null;
      }
    } else if (codeLines !== null) {
      codeLines.push(line);
    } else if (line.startsWith('## ')) {
      els.push(<Typography key={i} sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--tx-hi)', mt: 1.5 }}>{line.slice(3)}</Typography>);
    } else if (line.startsWith('### ')) {
      els.push(<Typography key={i} sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#c7d2fe', mt: 1 }}>{line.slice(4)}</Typography>);
    } else if (line.match(/^[-*] /)) {
      els.push(
        <Stack key={i} direction="row" gap={0.75} alignItems="flex-start" sx={{ mt: 0.3 }}>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#6366f1', flexShrink: 0, mt: '8px' }} />
          <Typography sx={{ fontSize: '0.83rem', color: 'var(--tx-lo)', lineHeight: 1.65 }}>{renderInline(line.slice(2))}</Typography>
        </Stack>
      );
    } else if (line.match(/^\d+\. /)) {
      const m = line.match(/^(\d+)\. /);
      els.push(
        <Stack key={i} direction="row" gap={0.75} alignItems="flex-start" sx={{ mt: 0.3 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: 700, flexShrink: 0, minWidth: 18 }}>{m[1]}.</Typography>
          <Typography sx={{ fontSize: '0.83rem', color: 'var(--tx-lo)', lineHeight: 1.65 }}>{renderInline(line.slice(m[0].length))}</Typography>
        </Stack>
      );
    } else if (line.trim() === '') {
      els.push(<Box key={i} sx={{ height: 6 }} />);
    } else {
      els.push(<Typography key={i} sx={{ fontSize: '0.83rem', color: 'var(--tx-lo)', lineHeight: 1.65 }}>{renderInline(line)}</Typography>);
    }
  });
  return <Box>{els}</Box>;
}

// ─── Key Settings Dialog ──────────────────────────────────────────────────────
function KeySettingsDialog({ open, onClose, keyStatus, onKeySaved, onKeyDeleted }) {
  const [editing, setEditing] = useState(null);
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function startEdit(pid) {
    setEditing(pid);
    setFields(pid === 'bedrock' ? { access_key_id: '', secret_access_key: '', region: 'us-east-1' } : { api_key: '' });
    setErr('');
  }

  async function handleSave(pid) {
    setSaving(true); setErr('');
    try {
      await api.put('/api/llm-keys/' + pid, pid === 'bedrock' ? fields : { api_key: fields.api_key });
      onKeySaved(pid);
      setEditing(null);
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function handleDelete(pid) {
    try { await api.delete('/api/llm-keys/' + pid); onKeyDeleted(pid); }
    catch { setErr('Failed to remove'); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#0d1526', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 2 } }}>
      <DialogTitle sx={{ color: 'var(--tx-hi)', fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <SettingsIcon sx={{ color: '#6366f1', fontSize: 18 }} />
          LLM Providers &amp; API Keys
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
        <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.3)', mb: 2 }}>
          Keys are stored in Cloudflare KV and never exposed to the browser.
        </Typography>
        <Stack gap={1.5}>
          {Object.entries(PROVIDERS).map(([pid, prov]) => {
            const hasKey = keyStatus[pid];
            const isEditing = editing === pid;
            const isBedrock = pid === 'bedrock';
            return (
              <Box key={pid} sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid ' + (isEditing ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.07)'), borderRadius: 1.5, p: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={isEditing ? 1.5 : 0}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: prov.color }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--tx-md)' }}>{prov.label}</Typography>
                    {isBedrock && <Chip label="AWS SigV4" size="small" sx={{ height: 16, fontSize: '0.58rem', bgcolor: 'rgba(255,153,0,0.12)', color: '#FF9900', border: '1px solid rgba(255,153,0,0.3)' }} />}
                    {hasKey ? <CheckCircleIcon sx={{ fontSize: 14, color: '#34d399' }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.2)' }} />}
                  </Stack>
                  <Stack direction="row" gap={0.5}>
                    {hasKey && !isEditing && (
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => handleDelete(pid)} sx={{ color: 'rgba(255,255,255,0.25)', '&:hover': { color: '#fb7185' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!isEditing && (
                      <Button size="small" variant="outlined" onClick={() => startEdit(pid)}
                        sx={{ fontSize: '0.7rem', color: '#818cf8', borderColor: 'rgba(99,102,241,0.35)', py: 0.3 }}>
                        {hasKey ? 'Replace' : 'Add'}
                      </Button>
                    )}
                  </Stack>
                </Stack>
                {isEditing && !isBedrock && (
                  <Stack direction="row" gap={1} alignItems="center">
                    <TextField autoFocus size="small" fullWidth type="password"
                      placeholder={'Paste ' + prov.label + ' API key'}
                      value={fields.api_key || ''}
                      onChange={e => setFields({ api_key: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') handleSave(pid); if (e.key === 'Escape') setEditing(null); }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', color: 'var(--tx-md)', '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' } } }}
                    />
                    <Button size="small" variant="contained" onClick={() => handleSave(pid)}
                      disabled={saving || !fields.api_key?.trim()}
                      sx={{ bgcolor: '#6366f1', fontSize: '0.72rem', whiteSpace: 'nowrap', minWidth: 56 }}>
                      {saving ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                    <Button size="small" onClick={() => setEditing(null)} sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>Cancel</Button>
                  </Stack>
                )}
                {isEditing && isBedrock && (
                  <Stack gap={1.25}>
                    <TextField autoFocus size="small" fullWidth label="AWS Access Key ID" placeholder="AKIA..."
                      value={fields.access_key_id || ''}
                      onChange={e => setFields(f => ({ ...f, access_key_id: e.target.value }))}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', color: 'var(--tx-md)' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', color: 'var(--tx-dim)' } }}
                    />
                    <TextField size="small" fullWidth type="password" label="AWS Secret Access Key"
                      value={fields.secret_access_key || ''}
                      onChange={e => setFields(f => ({ ...f, secret_access_key: e.target.value }))}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', color: 'var(--tx-md)' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', color: 'var(--tx-dim)' } }}
                    />
                    <Stack direction="row" gap={1} alignItems="flex-start">
                      <TextField size="small" label="AWS Region" placeholder="us-east-1"
                        value={fields.region || 'us-east-1'}
                        onChange={e => setFields(f => ({ ...f, region: e.target.value }))}
                        sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontSize: '0.78rem', color: 'var(--tx-md)' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', color: 'var(--tx-dim)' } }}
                      />
                      <Stack direction="row" gap={0.75} pt={0.5}>
                        <Button size="small" variant="contained" onClick={() => handleSave(pid)}
                          disabled={saving || !fields.access_key_id?.trim() || !fields.secret_access_key?.trim() || !fields.region?.trim()}
                          sx={{ bgcolor: '#6366f1', fontSize: '0.72rem', whiteSpace: 'nowrap', minWidth: 56 }}>
                          {saving ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : 'Save'}
                        </Button>
                        <Button size="small" onClick={() => setEditing(null)} sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>Cancel</Button>
                      </Stack>
                    </Stack>
                    <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-ft)', lineHeight: 1.5 }}>
                      IAM user needs bedrock:InvokeModel permission. Signed server-side with AWS SigV4.
                    </Typography>
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── History Sidebar ──────────────────────────────────────────────────────────
function HistorySidebar({ open, conversations, activeId, onSelect, onDelete, onNew, onClose, loading, isMobile }) {
  return (
    <>
      {isMobile && open && (
        <Box onClick={() => {}} sx={{ position: 'fixed', inset: 0, zIndex: 1199, bgcolor: 'rgba(0,0,0,0.5)' }} />
      )}
      <Box sx={{
        ...(isMobile ? {
          position: 'fixed', left: open ? 0 : -280, top: 52, bottom: 0, zIndex: 1200,
          transition: 'left 0.25s ease', width: 260,
        } : {
          width: open ? 260 : 0,
          minWidth: open ? 260 : 0,
          overflow: 'hidden',
          transition: 'width 0.22s ease, min-width 0.22s ease',
          flexShrink: 0,
          borderRight: '1px solid rgba(255,255,255,0.07)',
        }),
        bgcolor: 'var(--bg-surf)',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button fullWidth size="small" startIcon={<AddIcon sx={{ fontSize: 15 }} />} onClick={onNew}
          sx={{ justifyContent: 'flex-start', color: '#818cf8', bgcolor: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 1.5, fontSize: '0.75rem', py: 0.75, '&:hover': { bgcolor: 'rgba(99,102,241,0.14)' } }}>
          New Chat
        </Button>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'rgba(255,255,255,0.7)' }, flexShrink: 0 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <CircularProgress size={18} sx={{ color: '#6366f1' }} />
          </Box>
        )}
        {!loading && conversations.length === 0 && (
          <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-gh)', textAlign: 'center', mt: 3 }}>No conversations yet</Typography>
        )}
        <Stack gap={0.5}>
          {conversations.map(conv => {
            const isActive = conv.id === activeId;
            const providerColor = PROVIDERS[conv.provider]?.color || '#6366f1';
            return (
              <Box key={conv.id}
                onClick={() => onSelect(conv.id)}
                sx={{
                  px: 1.25, py: 1, borderRadius: 1.5, cursor: 'pointer',
                  bgcolor: isActive ? 'rgba(99,102,241,0.14)' : 'transparent',
                  border: '1px solid ' + (isActive ? 'rgba(99,102,241,0.3)' : 'transparent'),
                  '&:hover': { bgcolor: isActive ? 'rgba(99,102,241,0.14)' : 'rgba(255,255,255,0.04)' },
                  position: 'relative',
                  '&:hover .del-btn': { opacity: 1 },
                }}>
                <Typography sx={{ fontSize: '0.78rem', color: isActive ? 'var(--tx-md)' : 'var(--tx-lo)', lineHeight: 1.35, pr: 2.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {conv.title || 'Untitled'}
                </Typography>
                <Stack direction="row" alignItems="center" gap={0.75} mt={0.5}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: providerColor, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-gh)' }}>{timeAgo(conv.updated_at)}</Typography>
                </Stack>
                <IconButton className="del-btn" size="small" onClick={e => { e.stopPropagation(); onDelete(conv.id); }}
                  sx={{ position: 'absolute', top: 6, right: 4, opacity: 0, transition: 'opacity 0.15s', color: 'rgba(255,255,255,0.25)', p: 0.25, '&:hover': { color: '#fb7185' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4o');
  const [keyStatus, setKeyStatus] = useState({ openai: false, anthropic: false, gemini: false, bedrock: false });
  const [keyOpen, setKeyOpen] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [systemOpen, setSystemOpen] = useState(false);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width:600px)');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get('/api/llm-keys').then(r => setKeyStatus(r.data.providers || {})).catch(() => {});
    loadHistory();
    if (isMobile) setSidebarOpen(false);
  }, []);

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const res = await api.get('/api/chat/history');
      setConversations(res.data.conversations || []);
    } catch {}
    finally { setHistoryLoading(false); }
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleProviderChange = useCallback(pid => {
    setProvider(pid);
    setModel(PROVIDERS[pid].models[0].id);
  }, []);

  function startNewChat() {
    setMessages([]);
    setActiveConvId(null);
    setError('');
    setTimeout(() => inputRef.current?.focus(), 50);
    if (isMobile) setSidebarOpen(false);
  }

  async function loadConversation(id) {
    try {
      const res = await api.get('/api/chat/history/' + id);
      const conv = res.data.conversation;
      setMessages(conv.messages || []);
      setActiveConvId(conv.id);
      if (PROVIDERS[conv.provider]) {
        setProvider(conv.provider);
        setModel(conv.model);
      }
      if (isMobile) setSidebarOpen(false);
    } catch {}
  }

  async function deleteConversation(id) {
    try {
      await api.delete('/api/chat/history/' + id);
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeConvId === id) startNewChat();
    } catch {}
  }

  async function saveConversation(newMessages, convId, currentProvider, currentModel, title) {
    const fallbackTitle = newMessages.find(m => m.role === 'user')?.content?.slice(0, 60) || 'Untitled';
    const finalTitle = title || fallbackTitle;
    try {
      const res = await api.post('/api/chat/history', {
        id: convId,
        title: finalTitle,
        provider: currentProvider,
        model: currentModel,
        messages: newMessages,
      });
      const savedId = res.data.id;
      setActiveConvId(savedId);
      setConversations(prev => {
        const meta = { id: savedId, title: finalTitle, provider: currentProvider, model: currentModel, message_count: newMessages.length, updated_at: new Date().toISOString() };
        const idx = prev.findIndex(c => c.id === savedId);
        if (idx >= 0) { const next = [...prev]; next[idx] = meta; return next; }
        return [meta, ...prev];
      });
      return savedId;
    } catch (e) {
      console.error('saveConversation failed:', e?.response?.data || e?.message || e);
      return convId;
    }
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;
    const newMsgs = [...messages, { role: 'user', content: text }];
    setMessages(newMsgs);
    setInput('');
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/chat', {
        messages: newMsgs, provider, model,
        system_prompt: systemPrompt.trim() || undefined,
      });
      const finalMsgs = [...newMsgs, { role: 'assistant', content: res.data.reply }];
      setMessages(finalMsgs);
      await saveConversation(finalMsgs, activeConvId, provider, model, res.data.suggested_title);
    } catch (e) {
      setError(e.response?.data?.error || 'Request failed');
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  const providerInfo = PROVIDERS[provider];
  const hasKey = keyStatus[provider];

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 52px)', bgcolor: 'var(--bg-base)', overflow: 'hidden' }}>
      {/* History sidebar */}
      <HistorySidebar
        open={sidebarOpen}
        conversations={conversations}
        activeId={activeConvId}
        onSelect={loadConversation}
        onDelete={deleteConversation}
        onNew={startNewChat}
        onClose={() => setSidebarOpen(false)}
        loading={historyLoading}
        isMobile={isMobile}
      />

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.07)', bgcolor: 'var(--bg-surf)', flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
            <Tooltip title={sidebarOpen ? 'Hide history' : 'Show history'}>
              <IconButton size="small" onClick={() => setSidebarOpen(o => !o)}
                sx={{ color: sidebarOpen ? '#818cf8' : 'rgba(255,255,255,0.35)', bgcolor: sidebarOpen ? 'rgba(99,102,241,0.1)' : 'transparent', borderRadius: 1.5, '&:hover': { color: '#a5b4fc' } }}>
                {sidebarOpen ? <ChevronLeftIcon sx={{ fontSize: 18 }} /> : <HistoryIcon sx={{ fontSize: 17 }} />}
              </IconButton>
            </Tooltip>

            <SmartToyIcon sx={{ color: '#6366f1', fontSize: 19 }} />
            <Typography sx={{ fontWeight: 700, color: 'var(--tx-hi)', fontSize: '0.95rem', mr: 0.5, display: { xs: 'none', sm: 'block' } }}>AI Chat</Typography>

            <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 148 } }}>
              <Select value={provider} onChange={e => handleProviderChange(e.target.value)} sx={selectSx}
                renderValue={v => (
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: PROVIDERS[v].color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-md)' }}>{PROVIDERS[v].label}</Typography>
                    {keyStatus[v] ? <CheckCircleIcon sx={{ fontSize: 12, color: '#34d399', ml: 'auto' }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', ml: 'auto' }} />}
                  </Stack>
                )}>
                {Object.entries(PROVIDERS).map(([pid, prov]) => (
                  <MenuItem key={pid} value={pid} sx={{ fontSize: '0.78rem' }}>
                    <Stack direction="row" alignItems="center" gap={1} width="100%">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: prov.color }} />
                      {prov.label}
                      {keyStatus[pid] ? <CheckCircleIcon sx={{ fontSize: 13, color: '#34d399', ml: 'auto' }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.2)', ml: 'auto' }} />}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 140, sm: 200 } }}>
              <Select value={model} onChange={e => setModel(e.target.value)} sx={selectSx}>
                {providerInfo.models.map(m => (
                  <MenuItem key={m.id} value={m.id} sx={{ fontSize: '0.78rem' }}>
                    {m.group
                      ? <Stack direction="row" alignItems="center" gap={1} width="100%">
                          <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-ft)', minWidth: 58 }}>{m.group}</Typography>
                          {m.label}
                        </Stack>
                      : m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1 }} />

            <Tooltip title={systemOpen ? 'Hide system prompt' : 'System prompt'}>
              <IconButton size="small" onClick={() => setSystemOpen(o => !o)}
                sx={{ color: systemPrompt ? '#818cf8' : 'rgba(255,255,255,0.35)', bgcolor: systemOpen ? 'rgba(99,102,241,0.12)' : 'transparent', borderRadius: 1.5, '&:hover': { color: '#a5b4fc' } }}>
                <TuneIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Manage API keys">
              <IconButton size="small" onClick={() => setKeyOpen(true)} sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: '#a5b4fc' } }}>
                <SettingsIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          {!hasKey && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}
              sx={{ bgcolor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.22)', borderRadius: 1, px: 1.5, py: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#fbbf24' }}>No credentials for {providerInfo.label}</Typography>
              <Button size="small" onClick={() => setKeyOpen(true)} sx={{ fontSize: '0.68rem', color: '#fbbf24', p: '2px 8px', minWidth: 0 }}>Configure</Button>
            </Stack>
          )}
        </Box>

        {/* System prompt */}
        <Collapse in={systemOpen}>
          <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(99,102,241,0.04)', flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
              <Typography sx={{ fontSize: '0.66rem', color: '#6366f1', fontWeight: 700, letterSpacing: 0.5 }}>SYSTEM PROMPT</Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-ft)' }}>Sent before every conversation</Typography>
              <Box sx={{ flex: 1 }} />
              <IconButton size="small" onClick={() => setSystemOpen(false)} sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#fff' } }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
            <TextField multiline minRows={2} maxRows={5} fullWidth size="small"
              placeholder="e.g. You are a concise assistant that always responds in bullet points..."
              value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.8rem', color: 'var(--tx-md)', bgcolor: 'rgba(255,255,255,0.03)',
                  '& fieldset': { borderColor: 'rgba(99,102,241,0.25)' },
                  '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
                '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(255,255,255,0.2)', opacity: 1 },
              }}
            />
          </Box>
        </Collapse>

        {/* Messages */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 6, lg: 10 }, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {messages.length === 0 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 8, opacity: 0.4, userSelect: 'none' }}>
              <SmartToyIcon sx={{ fontSize: 52, color: '#6366f1', mb: 1.5 }} />
              <Typography sx={{ color: 'var(--tx-dim)', fontSize: '0.9rem', fontWeight: 600 }}>Start a conversation</Typography>
              <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.78rem', mt: 0.5 }}>
                {providerInfo.label} · {providerInfo.models.find(m2 => m2.id === model)?.label || model}
              </Typography>
            </Box>
          )}

          {messages.map((msg, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-start', gap: 1.5 }}>
              {msg.role === 'assistant' && (
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: providerInfo.color }} />
                </Box>
              )}
              <Box sx={{
                maxWidth: msg.role === 'user' ? { xs: '88%', md: '70%' } : { xs: '95%', md: '82%' },
                ...(msg.role === 'user'
                  ? { bgcolor: 'rgba(99,102,241,0.16)', border: '1px solid rgba(99,102,241,0.28)', borderRadius: '16px 16px 4px 16px', px: 2, py: 1.25 }
                  : { bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px 16px 16px 16px', px: 2, py: 1.5 }
                ),
              }}>
                {msg.role === 'user'
                  ? <Typography sx={{ fontSize: '0.86rem', color: 'var(--tx-md)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                  : <MessageText text={msg.content} />}
              </Box>
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: providerInfo.color }} />
              </Box>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px 16px 16px 16px', px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={13} sx={{ color: '#6366f1' }} />
                <Typography sx={{ fontSize: '0.78rem', color: 'var(--tx-ft)' }}>Thinking...</Typography>
              </Box>
            </Box>
          )}

          {error && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Alert severity="error" sx={{ fontSize: '0.78rem', maxWidth: 520 }} onClose={() => setError('')}>{error}</Alert>
            </Box>
          )}
          <div ref={bottomRef} />
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

        {/* Input */}
        <Box sx={{ px: { xs: 2, md: 6, lg: 10 }, py: 2, flexShrink: 0, bgcolor: 'var(--bg-base)' }}>
          <Stack direction="row" gap={1.25} alignItems="flex-end">
            <TextField inputRef={inputRef} multiline maxRows={6} fullWidth
              placeholder={hasKey ? 'Message ' + providerInfo.label + '...' : 'Configure ' + providerInfo.label + ' credentials first'}
              value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              disabled={loading || !hasKey}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.88rem', color: 'var(--tx-md)', bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.35)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
                '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(255,255,255,0.2)', opacity: 1 },
              }}
            />
            <IconButton onClick={handleSend} disabled={!input.trim() || loading || !hasKey}
              sx={{ bgcolor: '#6366f1', color: '#fff', borderRadius: 2, width: 44, height: 44, flexShrink: 0, '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: 'rgba(99,102,241,0.2)', color: 'rgba(255,255,255,0.25)' } }}>
              {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Stack>
          <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.12)', mt: 0.75, textAlign: 'center' }}>
            Enter to send · Shift+Enter for new line
          </Typography>
        </Box>
      </Box>

      <KeySettingsDialog open={keyOpen} onClose={() => setKeyOpen(false)} keyStatus={keyStatus}
        onKeySaved={pid => setKeyStatus(prev => ({ ...prev, [pid]: true }))}
        onKeyDeleted={pid => setKeyStatus(prev => ({ ...prev, [pid]: false }))}
      />
    </Box>
  );
}
